import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Send, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const states = [
  "Delhi NCR", "Haryana", "Rajasthan", "Uttar Pradesh", "Maharashtra",
  "Gujarat", "Madhya Pradesh", "Karnataka", "Chhattisgarh", "West Bengal", "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    plant_location: "", monthly_bill: "", message: "",
    submission_type: "contact",
  });
  const [careerForm, setCareerForm] = useState({
    name: "", email: "", phone: "", message: "",
    submission_type: "career",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e, data, type) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(`${API}/api/contact`, data, { withCredentials: true });
      setSuccess(
        type === "contact"
          ? "Thank you! Our team will reach out shortly."
          : "Application received! We'll be in touch."
      );
      if (type === "contact") {
        setForm({
          name: "", company: "", email: "", phone: "",
          plant_location: "", monthly_bill: "", message: "",
          submission_type: "contact",
        });
      } else {
        setCareerForm({
          name: "", email: "", phone: "", message: "",
          submission_type: "career",
        });
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
              Get in Touch
            </p>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter text-white">
              Let's Power Your <span className="text-[#00D68F]">Future</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── Contact Form ─────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h2 className="text-3xl font-bold text-[#0A1628] mb-8">
              Request a Solar Assessment
            </h2>

            {success && (
              <div
                className="bg-[#00D68F]/10 text-[#00D68F] p-4 mb-6 font-bold text-sm"
                data-testid="contact-success"
              >
                {success}
              </div>
            )}
            {error && (
              <div
                className="bg-red-50 text-red-600 p-4 mb-6 font-bold text-sm"
                data-testid="contact-error"
              >
                {error}
              </div>
            )}

            <form
              onSubmit={(e) => handleSubmit(e, form, "contact")}
              className="space-y-4"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-name"
                  className="border-gray-200 bg-[#F5F7FA] h-12"
                />
                <Input
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="contact-company"
                  className="border-gray-200 bg-[#F5F7FA] h-12"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-email"
                  className="border-gray-200 bg-[#F5F7FA] h-12"
                />
                <Input
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone"
                  className="border-gray-200 bg-[#F5F7FA] h-12"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={form.plant_location}
                  onChange={(e) =>
                    setForm({ ...form, plant_location: e.target.value })
                  }
                  data-testid="contact-location"
                  className="h-12 px-3 border border-gray-200 bg-[#F5F7FA] text-sm text-[#0A1628] w-full focus:outline-none focus:ring-2 focus:ring-[#00D68F]"
                >
                  <option value="">Plant Location (State)</option>
                  {states.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Monthly Electricity Bill (Rs.)"
                  value={form.monthly_bill}
                  onChange={(e) =>
                    setForm({ ...form, monthly_bill: e.target.value })
                  }
                  data-testid="contact-bill"
                  className="border-gray-200 bg-[#F5F7FA] h-12"
                />
              </div>
              <textarea
                placeholder="Tell us about your energy needs..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="w-full border border-gray-200 bg-[#F5F7FA] px-3 py-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#00D68F]"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="bg-[#00D68F] text-[#0A1628] px-8 py-3 h-12 font-bold hover:-translate-y-0.5 transition-all w-full md:w-auto"
              >
                {loading ? "Sending..." : "Send Enquiry"}{" "}
                <Send size={16} className="ml-2" />
              </Button>
            </form>
          </div>

          <div className="md:col-span-5">
            <div className="bg-[#0A1628] p-8 mb-6">
              <h3 className="text-white font-bold text-lg mb-6">
                Contact Information
              </h3>
              <div className="space-y-4 text-white/70 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin
                    size={16}
                    className="text-[#00D68F] mt-0.5 shrink-0"
                  />
                  Unit 71, The Executive Centre, DLF One Horizon, Golf Course
                  Road, Gurugram, Haryana
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-[#00D68F]" /> info@energyx.in
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[#00D68F]" /> +91 98737
                  74484
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[#00D68F]" /> +91 70119
                  09630
                </div>
              </div>
            </div>
            {/* Static Map Placeholder */}
            <div className="bg-[#F5F7FA] h-64 flex items-center justify-center border border-gray-200 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-[#00D68F] to-[#0A1628]" />
              <div className="text-center text-[#0A1628]/40 relative z-10">
                <MapPin size={32} className="mx-auto mb-2" />
                <p className="text-sm font-bold">Gurugram, Haryana</p>
                <p className="text-xs">DLF One Horizon, Golf Course Road</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Careers ──────────────────────────────────────── */}
      <section
        className="py-24 md:py-32 bg-[#F5F7FA]"
        data-testid="careers-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Careers
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-6">
            Join the Clean Energy Revolution
          </h2>
          <p className="text-[#0A1628]/70 text-base md:text-lg mb-12">
            We're building the future of energy in India. If you're passionate
            about sustainability and want to make a real impact, we'd love to
            hear from you.
          </p>
          <form
            onSubmit={(e) => handleSubmit(e, careerForm, "career")}
            className="space-y-4 max-w-xl"
            data-testid="career-form"
          >
            <Input
              placeholder="Your Name *"
              value={careerForm.name}
              onChange={(e) =>
                setCareerForm({ ...careerForm, name: e.target.value })
              }
              required
              data-testid="career-name"
              className="border-gray-200 bg-white h-12"
            />
            <Input
              type="email"
              placeholder="Email *"
              value={careerForm.email}
              onChange={(e) =>
                setCareerForm({ ...careerForm, email: e.target.value })
              }
              required
              data-testid="career-email"
              className="border-gray-200 bg-white h-12"
            />
            <Input
              placeholder="Phone"
              value={careerForm.phone}
              onChange={(e) =>
                setCareerForm({ ...careerForm, phone: e.target.value })
              }
              data-testid="career-phone"
              className="border-gray-200 bg-white h-12"
            />
            <textarea
              placeholder="Tell us about yourself and what role you're interested in..."
              value={careerForm.message}
              onChange={(e) =>
                setCareerForm({ ...careerForm, message: e.target.value })
              }
              data-testid="career-message"
              className="w-full border border-gray-200 bg-white px-3 py-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#00D68F]"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="career-submit"
              className="bg-[#00D68F] text-[#0A1628] px-8 py-3 h-12 font-bold hover:-translate-y-0.5 transition-all"
            >
              Apply Now <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
